import PortalApp from 'portal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStoreDispatch, useAppStoreSelector } from '../../../store';
import { fetchPortalBasedOnId } from '../../app/api/portalAPI';
import { useToast } from '../../general/components/Toast.hook';
import {
  addActivePortal,
  fetchPortalsThunk,
  removeSelectedPortal,
} from '../store';
import Launcher from 'toolkit-template';
import usePageTitle from '../../../hooks/usePageTitle';
import Loading from '../../general/components/Loading';
import { truncateString } from '../../../utils/functions';
import { AxiosError } from 'axios';
import { useTheme } from '@mui/material/styles';
import { useColorMode } from '../../../theme/ColorModeContext';

function PortalRenderer() {
  const [portalConfig, setPortalConfig] = useState<any>(null);
  const [selectedFunction, setSelectedFunction] = useState<string | null>(null);
  const [parameterData, setParameterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const portalId = +(useLoaderData() as string);
  const { i18n } = useTranslation();
  const dispatch = useAppStoreDispatch();
  const [triggerMessageHandler] = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const portalState = useAppStoreSelector((state) => state.portal);
  const portal = portalState.data.portals.find((p) => p.portalID === portalId);
  const theme = useTheme();
  const { mode } = useColorMode();
  // console.log('Portal selected: ', portal);
  // const isFirstRender = useRef(true);
  const portalName = portal?.portalName;
  const [title] = usePageTitle(
    portalName ? truncateString(portalName, 10) : 'Portal'
  );
  const guestPortalsState = useAppStoreSelector(
    (state) => state.portal.data.guestPortals
  );

  // Check if there's a share parameter in the URL
  const shareParam = searchParams.get('share');
  const keyParam = searchParams.get('key');

  useEffect(() => {
    const fetchPortalConfig = async () => {
      setIsLoading(true);
      const isPortalGuest = guestPortalsState?.findIndex(
        (portal) => portal.portalID === portalId
      );
      const isGuest = isPortalGuest === -1 ? false : true;
      // console.log('Portal guest: ', isGuest);
      try {
        setPortalConfig(null);
        const fetchedPortalConfig = await fetchPortalBasedOnId(
          portalId,
          isGuest,
          shareParam,
          keyParam
        );
        console.log('Portal config: ', fetchedPortalConfig);
        setPortalConfig(fetchedPortalConfig);
        dispatch(addActivePortal({ id: portalId }));
        if (portalState.data.portals.length === 0) {
          console.log('Activate fetch portals');
          dispatch(fetchPortalsThunk());
        }
      } catch (error) {
        // console.log((error as AxiosError));
        //triggerMessageHandler((error as AxiosError).message, true);
        // User.expireUserSession();
        if ((error as AxiosError).status === 403 || (error as AxiosError).status === 401) {
          // Handle 403/401 errors - always redirect to auth
          const errorMessage = ((error as AxiosError).response?.data as any)?.message || 'Authentication required';
          triggerMessageHandler(errorMessage, true);
          
          const matchResult = (error as AxiosError).request?.responseURL?.match(/\/portal\/\d+/);
          const redirectPath = matchResult ? matchResult[0] : '/'; 
          
          // Always redirect to auth, include share or key params if present
          let authUrl = `/auth?redirect=${redirectPath}`;
          if (shareParam) authUrl += `&share=${shareParam}`;
          if (keyParam) authUrl += `&key=${keyParam}`;
          navigate(authUrl);
        } else {
          // For other errors, show message and redirect to home
          const errorMessage = ((error as AxiosError).response?.data as any)?.message || 'An error occurred while loading the portal';
          triggerMessageHandler(errorMessage, true);
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchPortalConfig();
    // window.addEventListener(Events.IMPORT_MODAL_STATE, (customEvent: Event) => {
    //   const { detail } = customEvent as CustomEvent;
    //   dispatch(storeImportModalData({ portalId, data: detail }));
    // });
    return () => {
      setPortalConfig(null);
      dispatch(removeSelectedPortal());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalId, shareParam, keyParam]);

  // useEffect(() => {
  //   console.log('Additional Fields POST: ', parameterData);
  // }, [parameterData]);

  if (isLoading && !portalConfig) {
    return <Loading />;
  } else if (portalConfig !== null) {
    return (
      <>
        {title}
        <PortalApp
          portalStationConfig={portalConfig}
          isPortalStation
          defaultLanguage={i18n.language}
          theme={theme}
          themeMode={mode}
          customerSpecificElement={
            <Launcher
              selectedFunction={selectedFunction!}
              parameterData={parameterData}
            />
          }
          setSelectedFunction={setSelectedFunction}
          setParameterData={setParameterData}
        />
      </>
    );
  } else {
    return null;
  }
}

export default PortalRenderer;
